// src/lib/intake/reviewAnswers.js
//
// Ticket 069 (decision D12). Pure: from the answers the page already holds, one
// row per QUESTION with the answer in plain words. Review renders only the
// client's own in-memory answers; nothing here is stored or sent to analytics.
//
// Row: { id, screen, fixScreen, section, question, answerText, status }
//   status: answered | none | open | not_sure | skipped
//   fixScreen: the screen the Edit / Fix link opens (null: no link, the tax year).
//
// Wording comes from the shared sources only (sharedOptions.js, slotConfig.js,
// intakeConfig.js, autonomoFactsConfig.js, incomeCardConfig.js). Not-applicable
// questions are not rows. The move-date placeholder prints as "Lived here since
// before {C}", never as 1900.

import { getSlotLabel } from '../slotConfig'
import {
  AGE_BAND_OPTIONS, AUTONOMO_TYPE_OPTIONS, BECKHAM_STATUS_OPTIONS, FOREIGN_ACCOUNT_BAND_OPTIONS,
  INCOME_PERIOD_OPTIONS, MAJOR_EVENT_OPTIONS, NET_WORTH_BAND_OPTIONS, PROPERTY_LOCATION_OPTIONS,
  COUNTRY_OPTIONS,
} from '../sharedOptions'
import { INCOME_CARD_CONFIG } from '../../hooks/incomeCardConfig'
import { AUTONOMO_QUESTIONS } from '../../hooks/autonomoFactsConfig'
import {
  SECTION1_SCREENS, SECTION23_SCREENS, FOLLOW_UP_TEXT, COUNT_ROWS, DISABILITY_LABEL, FILING_OPTIONS,
  GESTOR_QUESTION, GESTOR_OPTIONS, MONTH_NAMES, moveBeforeLabel, EVENT_NONE_LABEL, OWNED_PROPERTY_LABEL,
  OWNED_SPAIN_LABEL, OWNED_OUTSIDE_LABEL, SPOUSE_HAS_INCOME_QUESTION, NET_WORTH_QUESTION,
  FOREIGN_ACCOUNTS_QUESTION, REVIEW_NOT_ANSWERED, REVIEW_SKIPPED, REVIEW_NOT_SURE, REVIEW_YES, REVIEW_NO,
  REVIEW_NONE_INCOME, REVIEW_NO_AMOUNT, ALL_GROUP_KEYS,
} from './intakeConfig'
import { applicableKeys } from './section1Facts'
import { shouldShowScreen, touchedIn } from './section23Facts'

const labelOf = (options, value) => {
  const o = options.find((x) => x.value === value)
  return o ? o.label : String(value)
}
const known = (f) => !!f && f.status === 'known_value'
const isUnknown = (f) => !!f && f.status === 'unknown'

function row(id, screen, section, question, answerText, status, fixScreen = screen) {
  return { id, screen, fixScreen, section, question, answerText, status }
}

// A single fact answered from a list of options (slot labels or option arrays).
function factRow(id, screen, question, fact, labelFor) {
  if (known(fact)) return row(id, screen, 1, question, labelFor(fact.value), 'answered')
  if (isUnknown(fact)) return row(id, screen, 1, question, REVIEW_NOT_SURE, 'not_sure')
  return row(id, screen, 1, question, REVIEW_NOT_ANSWERED, 'open')
}

function moveText(move, today) {
  if (move.branch === 'before') return moveBeforeLabel(today)
  if (move.branch === 'recent' && move.year && move.month) {
    const month = MONTH_NAMES[Number(move.month) - 1]
    if (!month) return null
    return move.day ? `${month} ${Number(move.day)}, ${move.year}` : `${month} ${move.year}`
  }
  return null
}

function section1Rows(answers, taxYear, today) {
  const f = answers.facts || {}
  const applies = applicableKeys(f)
  const rows = []

  const mv = moveText(answers.move, today)
  rows.push(mv
    ? row('move_date', '1.2', 1, SECTION1_SCREENS['1.2'].title, mv, 'answered')
    : row('move_date', '1.2', 1, SECTION1_SCREENS['1.2'].title, REVIEW_NOT_ANSWERED, 'open'))

  // The tax year has no Edit link: a different year is a different saved intake.
  rows.push(taxYear != null
    ? row('tax_year', '1.1', 1, SECTION1_SCREENS['1.1'].title, String(taxYear), 'answered', null)
    : row('tax_year', '1.1', 1, SECTION1_SCREENS['1.1'].title, REVIEW_NOT_ANSWERED, 'open', null))

  rows.push(factRow('comunidad_autonoma', '1.3', SECTION1_SCREENS['1.3'].title, f.comunidad_autonoma, (v) => getSlotLabel('comunidad_autonoma', v)))
  rows.push(factRow('citizenship_status', '1.4', SECTION1_SCREENS['1.4'].title, f.citizenship_status, (v) => getSlotLabel('citizenship_status', v)))
  if (applies.eu_citizen) rows.push(factRow('eu_citizen', '1.4', FOLLOW_UP_TEXT.eu_citizen.question, f.eu_citizen, (v) => getSlotLabel('eu_citizen', v)))

  rows.push(factRow('marital_status', '1.5', SECTION1_SCREENS['1.5'].title, f.marital_status, (v) => getSlotLabel('marital_status', v)))
  if (applies.asset_split) rows.push(factRow('asset_split', '1.5', FOLLOW_UP_TEXT.asset_split.question, f.asset_split, (v) => getSlotLabel('asset_split', v)))
  if (applies.spouse_citizenship_status) rows.push(factRow('spouse_citizenship_status', '1.5', FOLLOW_UP_TEXT.spouse_citizenship_status.question, f.spouse_citizenship_status, (v) => getSlotLabel('citizenship_status', v)))
  if (applies.spouse_eu_citizen) rows.push(factRow('spouse_eu_citizen', '1.5', FOLLOW_UP_TEXT.spouse_eu_citizen.question, f.spouse_eu_citizen, (v) => getSlotLabel('eu_citizen', v)))

  rows.push(factRow('age_band', '1.6', SECTION1_SCREENS['1.6'].title, f.age_band, (v) => labelOf(AGE_BAND_OPTIONS, v)))
  if (applies.spouse_age_band) rows.push(factRow('spouse_age_band', '1.6', FOLLOW_UP_TEXT.spouse_age_band.question, f.spouse_age_band, (v) => labelOf(AGE_BAND_OPTIONS, v)))

  for (const c of COUNT_ROWS) {
    // A subset count only exists while its total is a number above zero.
    if (c.whole && !(known(f[c.whole]) && f[c.whole].value > 0)) continue
    const fact = f[c.key]
    if (fact && fact.status === 'known_zero') rows.push(row(c.key, '1.7', 1, c.label, '0', 'none'))
    else rows.push(factRow(c.key, '1.7', c.label, fact, (v) => String(v)))
  }
  rows.push(factRow('disability_taxpayer', '1.7', DISABILITY_LABEL, f.disability_taxpayer, (v) => (v ? REVIEW_YES : REVIEW_NO)))

  const filing = answers.filing || { filed: null, gestor: null }
  const filedQuestion = SECTION1_SCREENS['5.1'].title
  if (filing.filed === null) rows.push(row('filed_spain_before', '5.1', 1, filedQuestion, REVIEW_NOT_ANSWERED, 'open'))
  else rows.push(row('filed_spain_before', '5.1', 1, filedQuestion, labelOf(FILING_OPTIONS, filing.filed), 'answered'))
  if (filing.filed === true) {
    rows.push(filing.gestor === null
      ? row('has_gestor', '5.1', 1, GESTOR_QUESTION, REVIEW_NOT_ANSWERED, 'open')
      : row('has_gestor', '5.1', 1, GESTOR_QUESTION, labelOf(GESTOR_OPTIONS, filing.gestor), 'answered'))
  }
  return rows
}

// ── Money ────────────────────────────────────────────────────────────────────

const cardConfig = (type) => INCOME_CARD_CONFIG.find((c) => c.income_type === type)

function amountText(item) {
  const isNet = item.income_type === 'rental' || item.income_type === 'self_employment'
  const amount = isNet ? item.net_amount_original : item.gross_amount_original
  if (amount === null || amount === undefined || amount === '') return REVIEW_NO_AMOUNT
  const period = INCOME_PERIOD_OPTIONS.find((p) => p.value === item.amount_period)
  const money = `${Number(amount).toLocaleString('en')} ${item.currency || 'EUR'}`
  return period ? `${money} ${period.label}` : money
}

function cardRows(items, screen) {
  return items.map((item) => {
    const cfg = cardConfig(item.income_type)
    const kind = cfg ? cfg.label : item.income_type
    const name = item.label && item.label !== kind ? `${kind}: ${item.label}` : kind
    return row(`card:${item.id}`, screen, 2, name, amountText(item), 'answered')
  })
}

function eventsText(events) {
  const ticked = MAJOR_EVENT_OPTIONS.filter((o) => events[o.value] === true).map((o) => o.label)
  return ticked
}

function ownershipText(own) {
  const parts = []
  if (own.spain) parts.push(OWNED_SPAIN_LABEL)
  if (own.outside) {
    const names = (own.countries || []).map((c) => labelOf(COUNTRY_OPTIONS, c))
    parts.push(names.length ? `${OWNED_OUTSIDE_LABEL}: ${names.join(', ')}` : OWNED_OUTSIDE_LABEL)
  }
  return parts.join('; ')
}

function section2Rows(answers, ctx) {
  const rows = []
  const events = answers.events

  // 2.1 events (one question, a checklist) and owned property (its own question)
  const q21 = SECTION23_SCREENS['2.1'].title
  if (events === null) {
    rows.push(row('events', '2.1', 2, q21, REVIEW_NOT_ANSWERED, 'open'))
  } else {
    const ticked = eventsText(events)
    rows.push(row('events', '2.1', 2, q21, ticked.length ? ticked.join('; ') : EVENT_NONE_LABEL, ticked.length ? 'answered' : 'none'))
  }
  if (answers.ownership) {
    rows.push(row('owned_property', '2.1', 2, OWNED_PROPERTY_LABEL, ownershipText(answers.ownership) || REVIEW_NOT_ANSWERED, answers.ownership.spain || answers.ownership.outside ? 'answered' : 'open'))
  }

  // 2.2 own income
  const q22 = SECTION23_SCREENS['2.2'].title
  const own = touchedIn(answers, 'self', ALL_GROUP_KEYS)
  if (answers.skipIncome) rows.push(row('income', '2.2', 2, q22, REVIEW_SKIPPED, 'skipped'))
  else if (own.length > 0) rows.push(...cardRows(own, '2.2'))
  else if (answers.none.self) rows.push(row('income', '2.2', 2, q22, REVIEW_NONE_INCOME, 'none'))
  else rows.push(row('income', '2.2', 2, q22, REVIEW_NOT_ANSWERED, 'open'))

  // 2.3 / 2.3s spouse
  if (shouldShowScreen('2.3', ctx)) {
    const gate = answers.spouseHasIncome
    rows.push(gate === null || gate === undefined
      ? row('spouse_gate', '2.3', 2, SPOUSE_HAS_INCOME_QUESTION, REVIEW_NOT_ANSWERED, 'open')
      : row('spouse_gate', '2.3', 2, SPOUSE_HAS_INCOME_QUESTION, gate ? REVIEW_YES : REVIEW_NO, 'answered'))
    if (gate === true) {
      const spouse = touchedIn(answers, 'spouse', ALL_GROUP_KEYS)
      if (spouse.length > 0) rows.push(...cardRows(spouse, '2.3s'))
      else if (answers.skipIncome) rows.push(row('spouse_income', '2.2', 2, SECTION23_SCREENS['2.3s'].title, REVIEW_SKIPPED, 'skipped', '2.2'))
      else rows.push(row('spouse_income', '2.3s', 2, SECTION23_SCREENS['2.3s'].title, REVIEW_NOT_ANSWERED, 'open'))
    }
  }

  // 2.4 autónomo
  if (shouldShowScreen('2.4', ctx)) {
    const a = answers.autonomo || {}
    rows.push(a.autonomo_type
      ? row('autonomo_type', '2.4', 2, AUTONOMO_QUESTIONS.type, labelOf(AUTONOMO_TYPE_OPTIONS, a.autonomo_type), 'answered')
      : row('autonomo_type', '2.4', 2, AUTONOMO_QUESTIONS.type, REVIEW_NOT_ANSWERED, 'open'))
    rows.push(typeof a.registered_reta_last_2_years === 'boolean'
      ? row('registered_reta', '2.4', 2, AUTONOMO_QUESTIONS.reta, a.registered_reta_last_2_years ? REVIEW_YES : REVIEW_NO, 'answered')
      : row('registered_reta', '2.4', 2, AUTONOMO_QUESTIONS.reta, REVIEW_NOT_ANSWERED, 'open'))
    if (a.registered_reta_last_2_years === false) {
      rows.push(typeof a.previously_used_tarifa_plana === 'boolean'
        ? row('tarifa_plana', '2.4', 2, AUTONOMO_QUESTIONS.tarifa, a.previously_used_tarifa_plana ? REVIEW_YES : REVIEW_NO, 'answered')
        : row('tarifa_plana', '2.4', 2, AUTONOMO_QUESTIONS.tarifa, REVIEW_NOT_ANSWERED, 'open'))
    }
  }

  // 2.5 Beckham
  const q25 = SECTION23_SCREENS['2.5'].title
  rows.push(answers.beckham
    ? row('beckham', '2.5', 2, q25, labelOf(BECKHAM_STATUS_OPTIONS, answers.beckham), 'answered')
    : row('beckham', '2.5', 2, q25, REVIEW_NOT_ANSWERED, 'open'))

  // 3.3 sale location
  if (shouldShowScreen('3.3', ctx)) {
    const q33 = SECTION23_SCREENS['3.3'].title
    rows.push(answers.saleLocation
      ? row('sale_location', '3.3', 2, q33, labelOf(PROPERTY_LOCATION_OPTIONS, answers.saleLocation), 'answered')
      : row('sale_location', '3.3', 2, q33, REVIEW_NOT_ANSWERED, 'open'))
  }

  // 4.1 two questions, each its own row
  const a = answers.assets || {}
  rows.push(a.netWorth
    ? row('net_worth', '4.1', 2, NET_WORTH_QUESTION, labelOf(NET_WORTH_BAND_OPTIONS, a.netWorth), 'answered')
    : row('net_worth', '4.1', 2, NET_WORTH_QUESTION, REVIEW_NOT_ANSWERED, 'open'))
  rows.push(a.foreignAccounts
    ? row('foreign_accounts', '4.1', 2, FOREIGN_ACCOUNTS_QUESTION, labelOf(FOREIGN_ACCOUNT_BAND_OPTIONS, a.foreignAccounts), 'answered')
    : row('foreign_accounts', '4.1', 2, FOREIGN_ACCOUNTS_QUESTION, REVIEW_NOT_ANSWERED, 'open'))
  return rows
}

// input: { answers, taxYear, today, suggestions, dismissed }
export function buildReviewAnswers(input) {
  const { answers, taxYear = null, today = new Date(), suggestions = null, dismissed = [] } = input
  const ctx = { answers, suggestions, dismissed }
  return [...section1Rows(answers, taxYear, today), ...section2Rows(answers, ctx)]
}
